import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Check } from "lucide-react";

const empty = { name: "", price: 0, client_limit: 20, periodicity: "mensal", features: "", active: true };

export default function Plans() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/plans").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, features: p.features.join("\n") }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price), client_limit: parseInt(form.client_limit),
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean) };
    if (editing) await api.put(`/plans/${editing.id}`, payload);
    else await api.post("/plans", payload);
    toast.success("Plano salvo");
    setOpen(false); load();
  };

  const del = async (id) => { await api.delete(`/plans/${id}`); toast.success("Plano removido"); load(); };

  return (
    <div>
      <PageHeader title="Planos" subtitle="Gerencie os planos comercializados aos hipnoterapeutas"
        action={<Button onClick={openNew} className="rounded-full" data-testid="new-plan-btn"><Plus className="w-4 h-4 mr-1" />Novo plano</Button>} />
      <div className="grid md:grid-cols-3 gap-4">
        {list.map((p) => (
          <Card key={p.id} className="p-6 flex flex-col" data-testid={`plan-card-${p.id}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl font-medium">{p.name}</h3>
              {p.active && <span className="text-xs text-accent font-medium">Ativo</span>}
            </div>
            <div className="mt-2 font-heading text-3xl font-semibold text-primary">R$ {p.price.toFixed(2)}<span className="text-sm text-muted-foreground font-normal">/{p.periodicity}</span></div>
            <p className="text-sm text-muted-foreground mt-1">{p.client_limit === -1 ? "Clientes ilimitados" : `Até ${p.client_limit} clientes`}</p>
            <ul className="mt-4 space-y-1.5 flex-1">
              {p.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-accent mt-0.5 shrink-0" />{f}</li>)}
            </ul>
            <div className="flex gap-2 mt-5">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.id}`}><Pencil className="w-4 h-4 mr-1" />Editar</Button>
              <Button variant="outline" size="sm" className="text-destructive" onClick={() => del(p.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar plano" : "Novo plano"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name-input" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Preço (R$)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="plan-price-input" className="mt-1" /></div>
              <div><Label>Limite de clientes (-1 = ilimitado)</Label><Input type="number" value={form.client_limit} onChange={(e) => setForm({ ...form, client_limit: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Periodicidade</Label><Input value={form.periodicity} onChange={(e) => setForm({ ...form, periodicity: e.target.value })} className="mt-1" /></div>
            <div><Label>Recursos (um por linha)</Label><Textarea value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={4} className="mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Ativo</Label></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-plan-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
